/**
 * @component ry-accordion
 * @category  display
 * @summary   Vertically stacked collapsible sections (Fomantic UI `.ui.accordion`). Default is exclusive (one open at a time); set `multiple` to allow several panels open simultaneously. Authored as `<ry-accordion-item title="X" open>...` and transformed at setup time.
 *
 * Reference: Fomantic UI's `.ui.accordion` + accordion.js. Fomantic's
 * `exclusive` setting is inverted here: by default ry-accordion is exclusive;
 * the `multiple` attribute opts INTO Fomantic's `exclusive: false` behavior.
 *
 * @attribute multiple    boolean   default=false   Allow multiple items open at once. Default behavior is exclusive — opening item N closes the previously-open item.
 * @attribute styled      boolean   default=false   Apply chrome (border, shadow) per Fomantic's `.styled.accordion`. Pure visual; behavior unchanged.
 * @attribute fluid       boolean   default=false   Fill container width per Fomantic's `.fluid.accordion`. Pure visual; behavior unchanged.
 *
 * @property  multiple    boolean   Reflects the `multiple` attribute.
 *
 * @method    open(item)     Open the given item element. In exclusive mode, closes any other open items. Fires `ry:open`. Item must be a `[data-ry-target="item"]` descendant.
 * @method    close(item)    Close the given item. Fires `ry:close`.
 * @method    toggle(item)   Toggle the item — open if closed, close if open.
 * @method    openAll()      Open every item. In exclusive mode this is still permitted (programmatic API bypasses exclusivity).
 * @method    closeAll()     Close every item.
 *
 * @event ry:beforeopen
 *        detail: { item: HTMLElement }
 *        fires:  at the top of `open(item)` — trigger click, Enter/Space, `el.open(item)`, `openAll()` — before any state change (including the exclusive-mode auto-close of siblings)
 *        cancelable: preventDefault() keeps the item closed (and leaves siblings untouched); `ry:open` does not fire
 *
 * @event ry:open
 *        detail: { item: HTMLElement }
 *        fires:  after an item transitions to open
 *
 * @event ry:beforeclose
 *        detail: { item: HTMLElement }
 *        fires:  at the top of `close(item)` — trigger click, `el.close(item)`, `closeAll()`, or the exclusive-mode auto-close when a sibling opens — before any state change
 *        cancelable: preventDefault() keeps the item open; `ry:close` does not fire. In exclusive mode a vetoed auto-close leaves the sibling open while the newly-opened item still opens.

 *
 * @keyboard  ArrowDown — focus next trigger (wraps). ArrowUp — focus previous trigger (wraps). Home — focus first trigger. End — focus last trigger. Enter / Space — activate focused trigger (native button behavior).
 *
 * @state     Per-item `data-ry-state` (`"open"` / `"closed"`) on each `[data-ry-target="item"]`.
 *
 * @content   `<ry-accordion-item title="X" open?>` children. Each is transformed at setup time into a full trigger + panel structure. The `title` attribute becomes the visible header; everything inside becomes the panel content.
 *
 * @dom
 *   ry-accordion[multiple?]
 *     > div.ry-accordion__item     [data-ry-target="item"] [data-ry-state="open|closed"]
 *       > button.ry-accordion__trigger   [data-ry-target="trigger"] aria-expanded aria-controls=panel-id
 *         > {title text}
 *         > span.ry-accordion__icon      [data-ry-target="icon"]
 *       > div.ry-accordion__panel        [data-ry-target="panel"] role="region" aria-labelledby=trigger-id
 *         > div.ry-accordion__content   {child content}
 *     > div.ry-accordion__item    (additional items follow)
 *
 * @gotcha  Default behavior is EXCLUSIVE. This matches Fomantic's default. Set `multiple` to opt into a "checklist" accordion where any combination can be open. Naming is intentionally the inverse of Fomantic's `exclusive: false` option — the attribute is positive, not negative.
 *
 * @gotcha  Items are NOT slot children — `<ry-accordion-item>` markup is *transformed* (replaced) at setup() time via `outerHTML =`. After mount, the `<ry-accordion-item>` tag no longer exists in DOM. Use `[data-ry-target="item"]` to query item elements at runtime. Inserting new `<ry-accordion-item>` AFTER mount does not get transformed — re-set the accordion to refresh, or insert pre-transformed markup.
 *
 * @gotcha  `openAll()` and `closeAll()` bypass exclusivity. Don't expect openAll() to leave only one item open in exclusive mode — it opens them all, then exclusive mode only re-asserts on the next user click.
 *
 * @gotcha  Empty `title=""` shows literal "Untitled" — pass a non-empty title or override the trigger DOM manually.
 *
 * @example basic
 * ```html
 * <ry-accordion>
 *   <ry-accordion-item title="What is ry-ui?" open>
 *     A framework-agnostic Light DOM component library.
 *   </ry-accordion-item>
 *   <ry-accordion-item title="How do I install it?">
 *     <code>npm install @ryanhelsing/ry-ui</code>
 *   </ry-accordion-item>
 * </ry-accordion>
 * ```
 *
 * @example styled
 * ```html
 * <ry-accordion styled>
 *   <ry-accordion-item title="Section 1">Content A</ry-accordion-item>
 *   <ry-accordion-item title="Section 2">Content B</ry-accordion-item>
 * </ry-accordion>
 * ```
 *
 * @example fluid
 * ```html
 * <ry-accordion fluid styled>
 *   <ry-accordion-item title="Spans the container">Content</ry-accordion-item>
 * </ry-accordion>
 * ```
 *
 * @example multiple
 * ```html
 * <ry-accordion multiple>
 *   <ry-accordion-item title="A" open>Both A and C can be open simultaneously.</ry-accordion-item>
 *   <ry-accordion-item title="B">B stays closed while the others toggle independently.</ry-accordion-item>
 *   <ry-accordion-item title="C" open>C is open by default — try collapsing it.</ry-accordion-item>
 * </ry-accordion>
 * ```
 *
 * @example pre-opened
 * ```html
 * <ry-accordion>
 *   <ry-accordion-item title="Faq 1">A</ry-accordion-item>
 *   <ry-accordion-item title="Faq 2" open>B (this one starts open)</ry-accordion-item>
 *   <ry-accordion-item title="Faq 3">C</ry-accordion-item>
 * </ry-accordion>
 * ```
 *
 * @tutorial trigger-click-toggles
 * ```js
 * const el = mount('<ry-accordion><ry-accordion-item title="A">Body</ry-accordion-item></ry-accordion>');
 * const item = el.querySelector('[data-ry-target="item"]');
 * const trigger = item.querySelector('[data-ry-target="trigger"]');
 * await expectAttr(item, 'data-ry-state', 'closed', 'starts closed');
 * await expectEvent(el, 'ry:open', () => trigger.click());
 * await expectAttr(item, 'data-ry-state', 'open', 'click opens');
 * await expectEvent(el, 'ry:close', () => trigger.click());
 * await expectAttr(item, 'data-ry-state', 'closed', 'second click closes');
 * ```
 *
 * @tutorial exclusive-by-default
 * ```js
 * // Wrong assumption: every item operates independently. By default,
 * // opening item B closes item A. Fomantic-equivalent behavior.
 * const el = mount(`
 *   <ry-accordion>
 *     <ry-accordion-item title="A" open>A</ry-accordion-item>
 *     <ry-accordion-item title="B">B</ry-accordion-item>
 *   </ry-accordion>
 * `);
 * const [a, b] = el.querySelectorAll('[data-ry-target="item"]');
 * await expectAttr(a, 'data-ry-state', 'open');
 * await expectAttr(b, 'data-ry-state', 'closed');
 * b.querySelector('[data-ry-target="trigger"]').click();
 * await expectAttr(a, 'data-ry-state', 'closed', 'opening B auto-closes A (exclusive)');
 * await expectAttr(b, 'data-ry-state', 'open');
 * ```
 *
 * @tutorial multiple-allows-many-open
 * ```js
 * // [multiple] inverts the default — clicking B leaves A open.
 * const el = mount(`
 *   <ry-accordion multiple>
 *     <ry-accordion-item title="A" open>A</ry-accordion-item>
 *     <ry-accordion-item title="B">B</ry-accordion-item>
 *   </ry-accordion>
 * `);
 * const [a, b] = el.querySelectorAll('[data-ry-target="item"]');
 * b.querySelector('[data-ry-target="trigger"]').click();
 * await expectAttr(a, 'data-ry-state', 'open', 'A stays open under [multiple]');
 * await expectAttr(b, 'data-ry-state', 'open');
 * ```
 *
 * @tutorial aria-expanded-mirrors-state
 * ```js
 * // aria-expanded on the trigger MUST mirror the item's open/closed state
 * // for assistive tech. Verify both directions.
 * const el = mount(`
 *   <ry-accordion>
 *     <ry-accordion-item title="A">A</ry-accordion-item>
 *   </ry-accordion>
 * `);
 * const trigger = el.querySelector('[data-ry-target="trigger"]');
 * await expectAttr(trigger, 'aria-expanded', 'false');
 * trigger.click();
 * await expectAttr(trigger, 'aria-expanded', 'true');
 * trigger.click();
 * await expectAttr(trigger, 'aria-expanded', 'false');
 * ```
 *
 * @tutorial programmatic-open-close
 * ```js
 * // The .open(item) / .close(item) methods bypass exclusivity for the call
 * // itself but still emit ry:open / ry:close. .toggle(item) handles either direction.
 * const el = mount(`
 *   <ry-accordion>
 *     <ry-accordion-item title="A">A</ry-accordion-item>
 *     <ry-accordion-item title="B">B</ry-accordion-item>
 *   </ry-accordion>
 * `);
 * const [a, b] = el.querySelectorAll('[data-ry-target="item"]');
 * el.open(a);
 * await expectAttr(a, 'data-ry-state', 'open');
 * el.open(b);   // exclusive auto-closes A
 * await expectAttr(a, 'data-ry-state', 'closed', 'opening B closes A under exclusive');
 * el.toggle(b);
 * await expectAttr(b, 'data-ry-state', 'closed');
 * ```
 *
 * @tutorial keyboard-arrow-navigation
 * ```js
 * // Wrong assumption: arrow keys open the next item. They don't — they
 * // just MOVE FOCUS between trigger buttons (per WAI-ARIA accordion pattern).
 * // The user still needs Enter/Space to open.
 * const el = mount(`
 *   <ry-accordion>
 *     <ry-accordion-item title="A">A</ry-accordion-item>
 *     <ry-accordion-item title="B">B</ry-accordion-item>
 *     <ry-accordion-item title="C">C</ry-accordion-item>
 *   </ry-accordion>
 * `);
 * const triggers = el.querySelectorAll('[data-ry-target="trigger"]');
 * triggers[0].focus();
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowDown', bubbles: true }));
 * await expectEqual(document.activeElement, triggers[1], 'ArrowDown moves focus to next trigger');
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'End', bubbles: true }));
 * await expectEqual(document.activeElement, triggers[2], 'End focuses last trigger');
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'Home', bubbles: true }));
 * await expectEqual(document.activeElement, triggers[0], 'Home focuses first trigger');
 * // After ArrowDown past last, wraps to first
 * triggers[2].focus();
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowDown', bubbles: true }));
 * await expectEqual(document.activeElement, triggers[0], 'ArrowDown from last wraps to first');
 * ```
 *
 * @tutorial open-all-close-all
 * ```js
 * // openAll / closeAll bypass exclusivity. Wrong assumption: openAll
 * // respects exclusive and only opens the last one. It does NOT — it
 * // opens every item regardless. Mode is re-asserted on next user click.
 * const el = mount(`
 *   <ry-accordion>
 *     <ry-accordion-item title="A">A</ry-accordion-item>
 *     <ry-accordion-item title="B">B</ry-accordion-item>
 *     <ry-accordion-item title="C">C</ry-accordion-item>
 *   </ry-accordion>
 * `);
 * el.openAll();
 * const items = el.querySelectorAll('[data-ry-target="item"]');
 * for (const item of items) {
 *   await expectAttr(item, 'data-ry-state', 'open', 'openAll() opens every item');
 * }
 * el.closeAll();
 * for (const item of items) {
 *   await expectAttr(item, 'data-ry-state', 'closed', 'closeAll() closes every item');
 * }
 * ```
 *
 * @tutorial beforeopen-veto
 * ```js
 * // preventDefault() on ry:beforeopen keeps the item closed — whichever path
 * // asked (trigger click, el.open(item), openAll()). ry:open stays silent.
 * const el = mount(`
 *   <ry-accordion>
 *     <ry-accordion-item title="A">A</ry-accordion-item>
 *   </ry-accordion>
 * `);
 * const a = el.querySelector('[data-ry-target="item"]');
 * const opened = [];
 * el.addEventListener('ry:open', () => opened.push(1));
 * el.addEventListener('ry:beforeopen', (e) => e.preventDefault(), { once: true });
 * a.querySelector('[data-ry-target="trigger"]').click();
 * await expectAttr(a, 'data-ry-state', 'closed', 'vetoed open leaves the item closed');
 * await expectEqual(opened.length, 0, 'ry:open did not fire');
 * a.querySelector('[data-ry-target="trigger"]').click();
 * await expectAttr(a, 'data-ry-state', 'open', 'without the veto, the click opens');
 * await expectEqual(opened.length, 1);
 * ```
 *
 * @tutorial beforeclose-veto
 * ```js
 * // preventDefault() on ry:beforeclose keeps the item open. In exclusive
 * // mode the auto-close of a sibling ALSO passes through ry:beforeclose —
 * // vetoing it leaves A open while B still opens (two open in exclusive mode).
 * const el = mount(`
 *   <ry-accordion>
 *     <ry-accordion-item title="A" open>A</ry-accordion-item>
 *     <ry-accordion-item title="B">B</ry-accordion-item>
 *   </ry-accordion>
 * `);
 * const [a, b] = el.querySelectorAll('[data-ry-target="item"]');
 * const closed = [];
 * el.addEventListener('ry:close', () => closed.push(1));
 * el.addEventListener('ry:beforeclose', (e) => e.preventDefault(), { once: true });
 * el.close(a);
 * await expectAttr(a, 'data-ry-state', 'open', 'vetoed close leaves A open');
 * await expectEqual(closed.length, 0, 'ry:close did not fire');
 * // Exclusive auto-close: opening B asks A to close via the same guard.
 * el.addEventListener('ry:beforeclose', (e) => { if (e.detail.item === a) e.preventDefault(); }, { once: true });
 * b.querySelector('[data-ry-target="trigger"]').click();
 * await expectAttr(b, 'data-ry-state', 'open', 'B still opens');
 * await expectAttr(a, 'data-ry-state', 'open', 'A stays open — its auto-close was vetoed');
 * el.close(a);
 * await expectAttr(a, 'data-ry-state', 'closed', 'without the veto, close() works');
 * ```
 *
 * @see ry-tabs
 * @see ry-dropdown
 */


import { RyElement } from '../core/ry-element.js';

interface AccordionItemEventDetail {
  item: HTMLElement;
}

export class RyAccordion extends RyElement {
  setup(): void {
    // Transform minimal markup to full structure
    this.#transformMinimalMarkup();

    // Bind click handlers to all triggers
    this.$$('[data-ry-target="trigger"]').forEach(trigger => {
      this.on(trigger, 'click', this.#handleTriggerClick);
    });

    // Keyboard navigation
    this.on(this, 'keydown', this.#handleKeydown);

    // Initialize ARIA
    this.#initAria();
  }

  #transformMinimalMarkup(): void {
    // Convert <ry-accordion-item> to full structure
    // data-ry-target for JS queries, class for CSS styling
    this.$$('ry-accordion-item').forEach((item) => {
      const title = item.getAttribute('title') || 'Untitled';
      const isOpen = item.hasAttribute('open');
      const content = item.innerHTML;

      item.outerHTML = `
        <div data-ry-target="item" class="ry-accordion__item" data-ry-state="${isOpen ? 'open' : 'closed'}">
          <button data-ry-target="trigger" class="ry-accordion__trigger" aria-expanded="${isOpen}">
            ${title}
            <span data-ry-target="icon" class="ry-accordion__icon">&#9660;</span>
          </button>
          <div data-ry-target="panel" class="ry-accordion__panel">
            <div class="ry-accordion__content">${content}</div>
          </div>
        </div>
      `;
    });
  }

  #initAria(): void {
    this.$$('[data-ry-target="item"]').forEach((item, index) => {
      const trigger = item.querySelector('[data-ry-target="trigger"]');
      const panel = item.querySelector('[data-ry-target="panel"]');

      if (!trigger || !panel) return;

      // Generate IDs if not present
      const triggerId = trigger.id || `ry-accordion-trigger-${this.id || ''}-${index}`;
      const panelId = panel.id || `ry-accordion-panel-${this.id || ''}-${index}`;

      trigger.id = triggerId;
      panel.id = panelId;

      trigger.setAttribute('aria-controls', panelId);
      panel.setAttribute('aria-labelledby', triggerId);
      panel.setAttribute('role', 'region');

      // Set initial state
      const isOpen = (item as HTMLElement).dataset.ryState === 'open';
      trigger.setAttribute('aria-expanded', String(isOpen));
    });
  }

  #handleTriggerClick = (event: Event): void => {
    const trigger = event.currentTarget as HTMLElement;
    const item = trigger.closest('[data-ry-target="item"]') as HTMLElement | null;

    if (!item) return;

    this.toggle(item);
  };

  #handleKeydown = (event: KeyboardEvent): void => {
    const triggers = this.$$<HTMLElement>('[data-ry-target="trigger"]');
    const currentIndex = triggers.indexOf(document.activeElement as HTMLElement);

    if (currentIndex === -1) return;

    let newIndex: number;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newIndex = (currentIndex + 1) % triggers.length;
        triggers[newIndex]?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        newIndex = (currentIndex - 1 + triggers.length) % triggers.length;
        triggers[newIndex]?.focus();
        break;
      case 'Home':
        event.preventDefault();
        triggers[0]?.focus();
        break;
      case 'End':
        event.preventDefault();
        triggers[triggers.length - 1]?.focus();
        break;
    }
  };

  toggle(item: HTMLElement): void {
    const isOpen = item.dataset.ryState === 'open';

    if (isOpen) {
      this.close(item);
    } else {
      this.open(item);
    }
  }

  open(item: HTMLElement, options: { exclusive?: boolean } = {}): void {
    if (!this.emit<AccordionItemEventDetail>('beforeopen', { item })) return;

    const exclusive = options.exclusive ?? !this.hasAttribute('multiple');
    if (exclusive) {

      this.$$<HTMLElement>('[data-ry-target="item"][data-ry-state="open"]').forEach(openItem => {
        if (openItem !== item) {
          this.close(openItem);
        }
      });
    }

    const trigger = item.querySelector('[data-ry-target="trigger"]');

    item.dataset.ryState = 'open';
    trigger?.setAttribute('aria-expanded', 'true');

    this.emit<AccordionItemEventDetail>('open', { item });
  }

  close(item: HTMLElement): void {
    if (!this.emit<AccordionItemEventDetail>('beforeclose', { item })) return;

    const trigger = item.querySelector('[data-ry-target="trigger"]');


    item.dataset.ryState = 'closed';
    trigger?.setAttribute('aria-expanded', 'false');

    this.emit<AccordionItemEventDetail>('close', { item });
  }

  openAll(): void {
    this.$$<HTMLElement>('[data-ry-target="item"]').forEach(item => this.open(item, { exclusive: false }));
  }

  closeAll(): void {
    this.$$<HTMLElement>('[data-ry-target="item"]').forEach(item => this.close(item));
  }
}

customElements.define('ry-accordion', RyAccordion);

// Register placeholder for minimal markup
if (!customElements.get('ry-accordion-item')) {
  customElements.define('ry-accordion-item', class extends HTMLElement {});
}
